/**
 * Turnstile execute-with-retry helpers (testable without DOM / Cloudflare).
 * Invisible widget + execution:'execute' so render does not auto-start the challenge.
 */

const DEFAULT_MAX_ATTEMPTS = 3;

/**
 * @param {{ sitekey: string, action: string, onToken: Function, onError: Function }} args
 */
function buildWidgetOptions({ sitekey, action, onToken, onError }) {
	return {
		sitekey,
		size: 'invisible',
		execution: 'execute',
		action,
		callback: onToken,
		'error-callback': () => onError(new Error('Turnstile widget error')),
		'expired-callback': () => onError(new Error('Turnstile token expired')),
	};
}

/**
 * @param {object} deps
 * @returns {() => void} start first attempt
 */
function createExecuteWithRetry(deps) {
	const {
		turnstile,
		sitekey,
		action,
		ensureHost,
		setWidgetId,
		destroyWidget,
		onToken,
		onError,
		schedule = (fn) => setTimeout(fn, 0),
		maxAttempts = DEFAULT_MAX_ATTEMPTS,
		debug = () => {},
	} = deps;

	let attempts = 0;
	let inFlight = false;
	let settled = false;

	const finishOk = (token) => {
		if (settled) return;
		settled = true;
		inFlight = false;
		onToken(token);
	};

	const finishErr = (err) => {
		if (settled) return;
		settled = true;
		inFlight = false;
		onError(err instanceof Error ? err : new Error(String(err)));
	};

	const failOrRetry = (err) => {
		if (settled) return;
		inFlight = false;
		if (attempts >= maxAttempts) {
			finishErr(
				err instanceof Error
					? err
					: new Error(
							'Human verification failed after multiple attempts. Please refresh the page and try again.'
						)
			);
			return;
		}
		debug(`Turnstile retry ${attempts}/${maxAttempts}: ${err && err.message}`);
		destroyWidget(turnstile);
		schedule(run);
	};

	const run = () => {
		if (settled || inFlight) return;
		attempts += 1;
		if (attempts > maxAttempts) {
			finishErr(
				new Error(
					'Human verification failed after multiple attempts. Please refresh the page and try again.'
				)
			);
			return;
		}

		try {
			destroyWidget(turnstile);
			inFlight = true;
			const opts = buildWidgetOptions({
				sitekey,
				action,
				onToken: finishOk,
				onError: failOrRetry,
			});
			const id = turnstile.render(ensureHost(), opts);
			setWidgetId(id);
			turnstile.execute(id, { action });
		} catch (err) {
			debug('Turnstile execute error: ' + (err && err.message));
			failOrRetry(err);
		}
	};

	// Stop the runner for good: pending retries and late widget callbacks
	// become no-ops, and neither onToken nor onError fires afterwards.
	run.cancel = () => {
		settled = true;
		inFlight = false;
	};

	return run;
}

export {
	DEFAULT_MAX_ATTEMPTS,
	buildWidgetOptions,
	createExecuteWithRetry,
};
